import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { PressRelease, PressReleaseInsert } from "@/types/pressReleases";
import { useToast } from "@/hooks/use-toast";

export const usePressReleases = (brandId: string | null) => {
  const [pressReleases, setPressReleases] = useState<PressRelease[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const { toast } = useToast();

  const fetchPressReleases = async () => {
    if (!brandId) {
      setPressReleases([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('cm_pressreleases')
        .select('*')
        .eq('brand_id', brandId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setPressReleases((data || []) as PressRelease[]);
    } catch (error) {
      console.error('Error fetching press releases:', error);
      setPressReleases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const createPressRelease = async (pressReleaseData: PressReleaseInsert): Promise<PressRelease | null> => {
    setIsCreating(true);
    try {
      const { data, error } = await supabase
        .from('cm_pressreleases')
        .insert(pressReleaseData)
        .select()
        .single();

      if (error) throw error;

      setPressReleases(prev => [data as PressRelease, ...prev]);
      return data as PressRelease;
    } catch (error) {
      console.error('Error creating press release:', error);
      toast({
        title: "Error",
        description: "Failed to create press release",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  const generatePressRelease = async (pressReleaseId: string): Promise<boolean> => {
    try {
      // Update status to processing
      const { error: updateError } = await supabase
        .from('cm_pressreleases')
        .update({ generation_status: 'processing' })
        .eq('id', pressReleaseId);

      if (updateError) throw updateError;

      // Call edge function
      const { data, error } = await supabase.functions.invoke('generate-press-release', {
        body: { pressReleaseId, brandId },
      });

      if (error) throw error;

      // Refresh the list to get updated data
      await fetchPressReleases();

      toast({
        title: "Success",
        description: "Press release generated successfully",
      });

      return true;
    } catch (error) {
      console.error('Error generating press release:', error);
      toast({
        title: "Error",
        description: "Failed to generate press release",
        variant: "destructive",
      });

      // Update status to failed
      await supabase
        .from('cm_pressreleases')
        .update({ 
          generation_status: 'failed',
          generation_error: error instanceof Error ? error.message : 'Unknown error'
        })
        .eq('id', pressReleaseId);

      await fetchPressReleases();
      return false;
    }
  };

  const updatePressRelease = async (id: string, content: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from('cm_pressreleases')
        .update({ generated_content: content })
        .eq('id', id);

      if (error) throw error;

      setPressReleases(prev => prev.map(pr => 
        pr.id === id ? { ...pr, generated_content: content } : pr
      ));
      
      toast({
        title: "Success",
        description: "Press release updated",
      });

      return true;
    } catch (error) {
      console.error('Error updating press release:', error);
      toast({
        title: "Error",
        description: "Failed to update press release",
        variant: "destructive",
      });
      return false;
    }
  };

  const deletePressRelease = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('cm_pressreleases')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setPressReleases(prev => prev.filter(pr => pr.id !== id));
      toast({
        title: "Success",
        description: "Press release deleted",
      });
    } catch (error) {
      console.error('Error deleting press release:', error);
      toast({
        title: "Error",
        description: "Failed to delete press release",
        variant: "destructive",
      });
    }
  };

  const approvePressRelease = async (id: string): Promise<void> => {
    try {
      const { error } = await supabase
        .from('cm_pressreleases')
        .update({ status: 'approved' })
        .eq('id', id);

      if (error) throw error;

      setPressReleases(prev => prev.map(pr => 
        pr.id === id ? { ...pr, status: 'approved' } : pr
      ));
      
      toast({
        title: "Success",
        description: "Press release approved",
      });
    } catch (error) {
      console.error('Error approving press release:', error);
      toast({
        title: "Error",
        description: "Failed to approve press release",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchPressReleases();
  }, [brandId]);

  return {
    pressReleases,
    isLoading,
    isCreating,
    refetch: fetchPressReleases,
    createPressRelease,
    generatePressRelease,
    updatePressRelease,
    deletePressRelease,
    approvePressRelease,
  };
};
